import {
    VideoSource,
    VideoSourceType,
    VideoQuality,
    VideoMetadata,
} from '@/interfaces/video';

/**
 * Check if a video URL is an HLS stream
 */
export const isHLSStream = (url: string): boolean => {
    if (!url) return false;

    // Check for HLS file extensions
    const hlsExtensions = ['.m3u8', '.m3u'];
    const hasHlsExtension = hlsExtensions.some((ext) =>
        url.toLowerCase().includes(ext)
    );

    // Check for HLS content type in URL
    const hasHlsContentType =
        url.toLowerCase().includes('application/vnd.apple.mpegurl') ||
        url.toLowerCase().includes('application/x-mpegurl');

    // Check for HLS manifest keywords
    const hasHlsKeywords =
        url.toLowerCase().includes('manifest') ||
        url.toLowerCase().includes('playlist') ||
        url.toLowerCase().includes('hls');

    return hasHlsExtension || hasHlsContentType || hasHlsKeywords;
};

/**
 * Check if browser supports HLS natively
 */
export const supportsHLS = (): boolean => {
    if (typeof document === 'undefined') return false;

    const video = document.createElement('video');
    return (
        video.canPlayType('application/vnd.apple.mpegurl') !== '' ||
        video.canPlayType('application/x-mpegURL') !== ''
    );
};

// Cache for HLS.js module to avoid repeated imports
let hlsModuleCache: any = null;

/**
 * Preload HLS.js module in the background
 */
export const preloadHLS = async (): Promise<void> => {
    if (hlsModuleCache) return;

    try {
        hlsModuleCache = await import('hls.js');
    } catch (error) {
        console.warn('Failed to preload HLS.js:', error);
    }
};

/**
 * Get HLS.js module with caching
 */
const getHLSModule = async (): Promise<any> => {
    if (hlsModuleCache) {
        return hlsModuleCache.default;
    }

    try {
        const hls = await import('hls.js');
        hlsModuleCache = hls;
        return hls.default;
    } catch (error) {
        console.error('Failed to import HLS.js:', error);
        throw new Error('HLS.js library could not be loaded');
    }
};

/**
 * Load HLS.js if needed and attach to video element
 */
export const loadHLS = async (
    videoElement: HTMLVideoElement,
    src: string,
    deviceType?: 'mobile' | 'desktop'
): Promise<void> => {
    if (!isHLSStream(src)) return;

    // If browser supports HLS natively, no need for HLS.js
    if (supportsHLS()) return;

    // Prevent multiple HLS initializations on the same video element
    if ((videoElement as any).hls) {
        return Promise.resolve();
    }

    // Add loading state to prevent multiple attempts
    if ((videoElement as any).hlsLoading) {
        return Promise.resolve();
    }

    (videoElement as any).hlsLoading = true;

    try {
        // Get HLS.js module with caching
        const Hls = await getHLSModule();

        if (!Hls || !Hls.isSupported()) {
            throw new Error('HLS.js is not supported in this browser');
        }

        // Get device capabilities for optimal quality selection
        const isMobile =
            deviceType === 'mobile' ||
            (typeof window !== 'undefined' && window.innerWidth <= 768);
        const screenWidth =
            typeof window !== 'undefined' ? window.innerWidth : 1920;
        const screenHeight =
            typeof window !== 'undefined' ? window.innerHeight : 1080;
        const pixelRatio =
            typeof window !== 'undefined' ? window.devicePixelRatio : 1;

        // Calculate optimal bitrate based on screen size and device type
        const screenPixels = screenWidth * screenHeight * pixelRatio;
        let defaultBitrate = 2000000; // 2Mbps default

        if (isMobile) {
            if (screenPixels <= 750000) {
                // Small mobile screens
                defaultBitrate = 800000; // 800kbps
            } else if (screenPixels <= 1500000) {
                // Medium mobile screens
                defaultBitrate = 1200000; // 1.2Mbps
            } else {
                // Large mobile screens
                defaultBitrate = 1800000; // 1.8Mbps
            }
        } else {
            if (screenPixels <= 2000000) {
                // Small desktop screens
                defaultBitrate = 2500000; // 2.5Mbps
            } else if (screenPixels <= 4000000) {
                // Medium desktop screens
                defaultBitrate = 4000000; // 4Mbps
            } else {
                // Large desktop screens
                defaultBitrate = 6000000; // 6Mbps
            }
        }

        const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false, // Disable for better performance on smaller devices
            backBufferLength: 15, // Further reduced from 30
            // Enhanced configuration for better buffer management
            maxBufferLength: 10, // Reduced from 15
            maxMaxBufferLength: 200, // Reduced from 300
            maxBufferSize: 20 * 1000 * 1000, // Reduced to 20MB from 30MB
            maxBufferHole: 0.05, // Reduced to prevent gaps
            highBufferWatchdogPeriod: 3, // Less aggressive monitoring
            nudgeOffset: 0.05, // Smaller nudge to prevent gaps
            nudgeMaxRetry: 3, // Reduced from 5
            maxFragLookUpTolerance: 0.05, // Tighter tolerance
            liveSyncDurationCount: 2, // Reduced from 3
            liveMaxLatencyDurationCount: 8, // Reduced from 10
            liveDurationInfinity: true,
            liveBackBufferLength: 15, // Reduced from 30
            // Error recovery settings
            fragLoadingMaxRetry: 2, // Reduced from 3
            fragLoadingRetryDelay: 500, // Reduced from 1000
            manifestLoadingMaxRetry: 2, // Reduced from 3
            manifestLoadingRetryDelay: 500, // Reduced from 1000
            levelLoadingMaxRetry: 2, // Reduced from 3
            levelLoadingRetryDelay: 500, // Reduced from 1000
            // Proactive buffer management
            maxStarvationDelay: 1, // More aggressive for faster response
            maxLoadingDelay: 1, // More aggressive for faster response
            // Enhanced adaptive bitrate settings for screen size
            abrEwmaDefaultEstimate: defaultBitrate,
            abrBandWidthFactor: 0.95,
            abrBandWidthUpFactor: 0.7,
            abrMaxWithRealBitrate: true,
            // Quality selection based on screen size
            abrEwmaFastLive: 3.0,
            abrEwmaSlowLive: 9.0,
            abrEwmaFastVoD: 2.0,
            abrEwmaSlowVoD: 9.0,
            // Fragment loading optimization
            fragLoadingTimeOut: 10000, // Reduced from 15000
            manifestLoadingTimeOut: 5000, // Reduced from 8000
            levelLoadingTimeOut: 5000, // Reduced from 8000
        });

        // Create a promise that resolves when HLS is ready
        const hlsReadyPromise = new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('HLS initialization timeout'));
            }, 8000); // Reduced from 15 seconds to 8 seconds

            hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                // HLS media attached successfully
            });

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                // HLS manifest parsed successfully
                clearTimeout(timeout);
                resolve();
            });

            hls.on(Hls.Events.ERROR, (event: any, data: any) => {
                console.error('HLS initialization error:', data);
                clearTimeout(timeout);
                reject(
                    new Error(
                        `HLS initialization failed: ${data?.details || 'Unknown error'}`
                    )
                );
            });
        });

        // Load source and attach media
        hls.loadSource(src);
        hls.attachMedia(videoElement);

        // Wait for HLS to be ready
        await hlsReadyPromise;

        // Quality selection based on screen size and device capabilities
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
            if (hls.levels && hls.levels.length > 0) {
                // Find the best quality level for the current screen
                const levels = hls.levels;

                // Calculate optimal resolution based on screen size
                const targetWidth = screenWidth;
                const targetHeight = screenHeight;

                // Define maximum resolution limits based on screen size
                let maxWidth = targetWidth;
                let maxHeight = targetHeight;
                let maxBitrate = defaultBitrate / 1000; // Convert to kbps

                // For small screens, limit resolution and bitrate
                if (screenWidth <= 1366) {
                    // Small desktop screens (1366x768, 1280x720, etc.)
                    maxWidth = 1280;
                    maxHeight = 720;
                    maxBitrate = 2000; // 2Mbps max for small desktop
                } else if (screenWidth <= 1920) {
                    // Medium desktop screens
                    maxWidth = 1920;
                    maxHeight = 1080;
                    maxBitrate = 4000; // 4Mbps max for medium desktop
                } else {
                    // Large desktop screens
                    maxWidth = 2560;
                    maxHeight = 1440;
                    maxBitrate = 6000; // 6Mbps max for large desktop
                }

                // Find the optimal level based on screen size and device capabilities
                let optimalLevel = 0;
                let bestScore = -Infinity;

                for (let i = 0; i < levels.length; i++) {
                    const level = levels[i];
                    const levelBitrate = level.bitrate / 1000; // Convert to kbps

                    // Skip levels that exceed our limits
                    if (
                        level.width > maxWidth ||
                        level.height > maxHeight ||
                        levelBitrate > maxBitrate
                    ) {
                        continue;
                    }

                    // Calculate resolution score (closer to screen size = better, but don't exceed limits)
                    const widthDiff = Math.abs(level.width - targetWidth);
                    const heightDiff = Math.abs(level.height - targetHeight);
                    const resolutionScore =
                        1 / (1 + (widthDiff + heightDiff) / 1000);

                    // Calculate bitrate score (appropriate for device type and screen size)
                    let bitrateScore = 0;
                    if (isMobile) {
                        if (levelBitrate <= 1200)
                            bitrateScore = 1; // Good for mobile
                        else if (levelBitrate <= 1800)
                            bitrateScore = 0.7; // Acceptable
                        else bitrateScore = 0.3; // Too high for mobile
                    } else {
                        // For desktop, be more conservative with bitrate based on screen size
                        if (screenWidth <= 1366) {
                            if (levelBitrate <= 1500)
                                bitrateScore = 1; // Good for small desktop
                            else if (levelBitrate <= 2000)
                                bitrateScore = 0.6; // Acceptable
                            else bitrateScore = 0.2; // Too high for small desktop
                        } else if (screenWidth <= 1920) {
                            if (levelBitrate <= 3000)
                                bitrateScore = 1; // Good for medium desktop
                            else if (levelBitrate <= 4000)
                                bitrateScore = 0.7; // Acceptable
                            else bitrateScore = 0.3; // Too high for medium desktop
                        } else {
                            if (levelBitrate <= 4000)
                                bitrateScore = 1; // Good for large desktop
                            else if (levelBitrate <= 6000)
                                bitrateScore = 0.8; // Acceptable
                            else bitrateScore = 0.4; // High but acceptable
                        }
                    }

                    // Combined score (resolution + bitrate)
                    const totalScore =
                        resolutionScore * 0.6 + bitrateScore * 0.4;

                    if (totalScore > bestScore) {
                        bestScore = totalScore;
                        optimalLevel = i;
                    }
                }

                // If no suitable level found, fall back to the lowest available
                if (bestScore === -Infinity) {
                    optimalLevel = 0;
                }

                // Set initial quality level and force it
                hls.currentLevel = optimalLevel;

                // For small desktop screens, completely disable adaptive bitrate
                if (screenWidth <= 1366) {
                    // Lock the quality level and disable all adaptive switching
                    hls.nextLevel = optimalLevel;
                    hls.autoLevelEnabled = false; // Disable automatic level switching

                    // Override the adaptive bitrate settings for small screens
                    hls.config.abrEwmaDefaultEstimate = 1500000; // Force lower bitrate estimate
                    hls.config.abrBandWidthFactor = 0.5; // More conservative bandwidth usage
                    hls.config.abrBandWidthUpFactor = 0.3; // Less aggressive up-switching
                } else {
                    // For larger screens, enable adaptive bitrate after a delay
                    hls.nextLevel = optimalLevel; // Force this level initially
                    setTimeout(() => {
                        hls.nextLevel = -1; // Enable auto level switching
                    }, 5000); // Wait 5 seconds before allowing auto-switching
                }
            }
        });

        // Monitor quality changes and adjust based on performance
        hls.on(Hls.Events.LEVEL_SWITCHED, (event: any, data: any) => {
            const newLevel = hls.levels[data.level];
            if (newLevel) {
                // For small screens, prevent switching to high quality levels
                if (screenWidth <= 1366) {
                    const levelBitrate = newLevel.bitrate / 1000;
                    const levelWidth = newLevel.width;
                    const levelHeight = newLevel.height;

                    // If switched to a level that's too high, force back to a lower level
                    if (
                        levelBitrate > 2000 ||
                        levelWidth > 1280 ||
                        levelHeight > 720
                    ) {
                        // Find a suitable lower level
                        for (let i = data.level - 1; i >= 0; i--) {
                            const lowerLevel = hls.levels[i];
                            if (
                                lowerLevel &&
                                lowerLevel.bitrate / 1000 <= 2000 &&
                                lowerLevel.width <= 1280 &&
                                lowerLevel.height <= 720
                            ) {
                                hls.nextLevel = i;
                                break;
                            }
                        }
                    }
                }
            }
        });

        // Handle screen size changes (orientation change, window resize)
        const handleScreenSizeChange = () => {
            if (hls.levels && hls.levels.length > 0) {
                const newScreenWidth = window.innerWidth;
                const newScreenHeight = window.innerHeight;

                // Define maximum resolution limits based on new screen size
                let maxWidth = newScreenWidth;
                let maxHeight = newScreenHeight;
                let maxBitrate = 4000; // Default 4Mbps

                // For small screens, limit resolution and bitrate
                if (newScreenWidth <= 1366) {
                    // Small desktop screens
                    maxWidth = 1280;
                    maxHeight = 720;
                    maxBitrate = 2000; // 2Mbps max for small desktop
                } else if (newScreenWidth <= 1920) {
                    // Medium desktop screens
                    maxWidth = 1920;
                    maxHeight = 1080;
                    maxBitrate = 4000; // 4Mbps max for medium desktop
                } else {
                    // Large desktop screens
                    maxWidth = 2560;
                    maxHeight = 1440;
                    maxBitrate = 6000; // 6Mbps max for large desktop
                }

                // Recalculate optimal level based on new screen size
                let optimalLevel = 0;
                let bestScore = -Infinity;

                for (let i = 0; i < hls.levels.length; i++) {
                    const level = hls.levels[i];
                    const levelBitrate = level.bitrate / 1000; // Convert to kbps

                    // Skip levels that exceed our limits
                    if (
                        level.width > maxWidth ||
                        level.height > maxHeight ||
                        levelBitrate > maxBitrate
                    ) {
                        continue;
                    }

                    const widthDiff = Math.abs(level.width - newScreenWidth);
                    const heightDiff = Math.abs(level.height - newScreenHeight);
                    const resolutionScore =
                        1 / (1 + (widthDiff + heightDiff) / 1000);

                    // Calculate bitrate score based on new screen size
                    let bitrateScore = 0;
                    if (newScreenWidth <= 1366) {
                        if (levelBitrate <= 1500) bitrateScore = 1;
                        else if (levelBitrate <= 2000) bitrateScore = 0.6;
                        else bitrateScore = 0.2;
                    } else if (newScreenWidth <= 1920) {
                        if (levelBitrate <= 3000) bitrateScore = 1;
                        else if (levelBitrate <= 4000) bitrateScore = 0.7;
                        else bitrateScore = 0.3;
                    } else {
                        if (levelBitrate <= 4000) bitrateScore = 1;
                        else if (levelBitrate <= 6000) bitrateScore = 0.8;
                        else bitrateScore = 0.4;
                    }

                    const totalScore =
                        resolutionScore * 0.6 + bitrateScore * 0.4;

                    if (totalScore > bestScore) {
                        bestScore = totalScore;
                        optimalLevel = i;
                    }
                }

                // If no suitable level found, fall back to the lowest available
                if (bestScore === -Infinity) {
                    optimalLevel = 0;
                }

                // Switch to optimal level if it's different
                if (hls.currentLevel !== optimalLevel) {
                    // For small desktop screens, be more conservative about quality switching
                    if (newScreenWidth <= 1366) {
                        // Only switch if the new level is significantly better and within limits
                        const currentLevel = hls.levels[hls.currentLevel];
                        const newLevel = hls.levels[optimalLevel];

                        if (currentLevel && newLevel) {
                            const currentBitrate = currentLevel.bitrate / 1000;
                            const newBitrate = newLevel.bitrate / 1000;

                            // Only switch if new level has lower bitrate or similar bitrate but better resolution
                            if (
                                newBitrate <= currentBitrate ||
                                (newBitrate <= 2000 &&
                                    newLevel.width <= 1280 &&
                                    newLevel.height <= 720)
                            ) {
                                hls.nextLevel = optimalLevel;
                            }
                        }
                    } else {
                        // For larger screens, allow normal quality switching
                        hls.nextLevel = optimalLevel;
                    }
                }
            }
        };

        // Add resize listener for dynamic quality adjustment
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleScreenSizeChange);
            window.addEventListener(
                'orientationchange',
                handleScreenSizeChange
            );
        }

        // Proactive buffer monitoring
        let bufferStallTimeout: NodeJS.Timeout | null = null;
        let gapPreventionInterval: NodeJS.Timeout | null = null;

        const startBufferMonitoring = () => {
            if (bufferStallTimeout) {
                clearTimeout(bufferStallTimeout);
            }

            bufferStallTimeout = setTimeout(() => {
                const currentTime = videoElement.currentTime;
                const buffered = videoElement.buffered;
                let hasBufferedAhead = false;

                if (buffered.length > 0) {
                    for (let i = 0; i < buffered.length; i++) {
                        if (buffered.end(i) > currentTime + 2) {
                            hasBufferedAhead = true;
                            break;
                        }
                    }
                }

                // If no buffered content ahead and video is playing, it might stall
                if (
                    !hasBufferedAhead &&
                    !videoElement.paused &&
                    !videoElement.ended
                ) {
                    if (hls.levels && hls.levels.length > 1) {
                        const currentLevel = hls.currentLevel;
                        const lowerLevel = Math.max(0, currentLevel - 1);

                        // For small desktop screens, only allow switching to lower quality
                        if (screenWidth <= 1366) {
                            if (lowerLevel !== currentLevel) {
                                const targetLevel = hls.levels[lowerLevel];
                                if (
                                    targetLevel &&
                                    targetLevel.bitrate / 1000 <= 2000
                                ) {
                                    hls.nextLevel = lowerLevel;
                                }
                            }
                        } else {
                            // For larger screens, allow normal quality switching
                            if (lowerLevel !== currentLevel) {
                                hls.nextLevel = lowerLevel;
                            }
                        }
                    }
                }

                // Continue monitoring
                startBufferMonitoring();
            }, 2000); // Reduced from 3 seconds to 2 seconds
        };

        // Start gap prevention monitoring
        const startGapPrevention = () => {
            if (gapPreventionInterval) {
                clearInterval(gapPreventionInterval);
            }

            gapPreventionInterval = setInterval(() => {
                // Only check if video is playing or about to play
                if (!videoElement.paused || videoElement.readyState >= 2) {
                    preventBufferGap(videoElement, hls);

                    // For small screens, periodically check and enforce quality limits
                    if (
                        screenWidth <= 1366 &&
                        hls.levels &&
                        hls.currentLevel !== -1
                    ) {
                        const currentLevel = hls.levels[hls.currentLevel];
                        if (currentLevel) {
                            const levelBitrate = currentLevel.bitrate / 1000;
                            const levelWidth = currentLevel.width;
                            const levelHeight = currentLevel.height;

                            // If current level exceeds limits, force to a lower level
                            if (
                                levelBitrate > 2000 ||
                                levelWidth > 1280 ||
                                levelHeight > 720
                            ) {
                                // Find the best suitable level within limits
                                let bestLevel = 0;
                                for (let i = 0; i < hls.levels.length; i++) {
                                    const level = hls.levels[i];
                                    if (
                                        level &&
                                        level.bitrate / 1000 <= 2000 &&
                                        level.width <= 1280 &&
                                        level.height <= 720
                                    ) {
                                        bestLevel = i;
                                    }
                                }
                                if (hls.currentLevel !== bestLevel) {
                                    hls.nextLevel = bestLevel;
                                }
                            }
                        }
                    }
                }
            }, 2000); // Check every 2 seconds instead of 500ms
        };

        // Start monitoring when video starts playing
        videoElement.addEventListener('play', () => {
            startBufferMonitoring();
            startGapPrevention();
        });
        videoElement.addEventListener('pause', () => {
            if (bufferStallTimeout) {
                clearTimeout(bufferStallTimeout);
                bufferStallTimeout = null;
            }
            if (gapPreventionInterval) {
                clearInterval(gapPreventionInterval);
                gapPreventionInterval = null;
            }
        });

        // Also start gap prevention when video is loaded
        videoElement.addEventListener('loadedmetadata', startGapPrevention);

        // Listen for stalled event specifically
        videoElement.addEventListener('stalled', () => {
            preventBufferGap(videoElement, hls);
        });

        // Listen for waiting event (when video is waiting for data)
        videoElement.addEventListener('waiting', () => {
            preventBufferGap(videoElement, hls);
        });

        // Comprehensive error handling
        hls.on(Hls.Events.ERROR, (event: any, data: any) => {
            console.error(
                `HLS playback error [type: ${data?.type}, details: ${data?.details}, fatal: ${data?.fatal}]`,
                data
            );

            // Handle specific error types
            switch (data?.details) {
                case Hls.ErrorDetails.BUFFER_STALLED_ERROR:
                    // Check if this is a low buffer stall specifically
                    if (
                        data?.fatal === false &&
                        data?.type === Hls.ErrorTypes.MEDIA_ERROR
                    ) {
                        handleLowBufferStall(hls, videoElement, data);
                    } else {
                        handleBufferStalledError(hls, videoElement);
                    }
                    break;

                case Hls.ErrorDetails.FRAG_LOAD_ERROR:
                    handleFragmentLoadError(hls, data);
                    break;

                case Hls.ErrorDetails.MANIFEST_LOAD_ERROR:
                    handleManifestLoadError(hls, data);
                    break;

                case Hls.ErrorDetails.LEVEL_LOAD_ERROR:
                    handleLevelLoadError(hls, data);
                    break;

                case Hls.ErrorDetails.KEY_LOAD_ERROR:
                    handleMediaError(hls, videoElement, data);
                    break;

                default:
                    console.error('Unhandled HLS error:', data);
                    break;
            }
        });

        // Store HLS instance and cleanup functions on video element
        (videoElement as any).hls = hls;
        (videoElement as any).hlsCleanup = () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', handleScreenSizeChange);
                window.removeEventListener(
                    'orientationchange',
                    handleScreenSizeChange
                );
            }

            // Clear monitoring intervals
            if (bufferStallTimeout) {
                clearTimeout(bufferStallTimeout);
                bufferStallTimeout = null;
            }
            if (gapPreventionInterval) {
                clearInterval(gapPreventionInterval);
                gapPreventionInterval = null;
            }
        };

        // Clear loading state
        (videoElement as any).hlsLoading = false;

        return Promise.resolve();
    } catch (error) {
        // Clear loading state on error
        (videoElement as any).hlsLoading = false;
        console.error('Failed to load HLS.js:', error);
        throw error;
    }
};

/**
 * Handle buffer stalled error
 */
const handleBufferStalledError = (hls: any, videoElement: HTMLVideoElement) => {
    try {
        const currentTime = videoElement.currentTime;
        const duration = videoElement.duration;
        const buffered = videoElement.buffered;

        // Check if we have buffered content ahead
        let hasBufferedAhead = false;
        if (buffered.length > 0) {
            for (let i = 0; i < buffered.length; i++) {
                if (buffered.end(i) > currentTime + 1) {
                    hasBufferedAhead = true;
                    break;
                }
            }
        }

        // Strategy 1: If we have buffered content ahead, seek forward
        if (hasBufferedAhead && currentTime < duration - 2) {
            const seekTime = Math.min(currentTime + 1, duration - 1);
            videoElement.currentTime = seekTime;
            return;
        }

        // Strategy 2: If near end, try to reload the stream
        if (currentTime >= duration - 2) {
            hls.destroy();
            setTimeout(() => {
                loadHLS(videoElement, videoElement.src).catch(console.error);
            }, 1000);
            return;
        }

        // Strategy 3: Try to switch to a lower quality level
        if (hls.levels && hls.levels.length > 1) {
            const currentLevel = hls.currentLevel;
            const lowerLevel = Math.max(0, currentLevel - 1);
            hls.nextLevel = lowerLevel;

            // Give it a moment, then try seeking
            setTimeout(() => {
                if (videoElement.paused) {
                    videoElement.currentTime = Math.min(
                        currentTime + 0.5,
                        duration - 1
                    );
                }
            }, 500);
            return;
        }

        // Strategy 4: Force reload the current fragment
        // Try to reload the current fragment by seeking slightly
        videoElement.currentTime = Math.min(currentTime + 0.1, duration - 1);
    } catch (error) {
        console.error('❌ Failed to handle buffer stalled error:', error);

        // Last resort: reload the entire stream
        try {
            hls.destroy();
            setTimeout(() => {
                loadHLS(videoElement, videoElement.src).catch(console.error);
            }, 2000);
        } catch (destroyError) {
            console.error('❌ Failed to destroy HLS instance:', destroyError);
        }
    }
};

/**
 * Handle fragment load error
 */
const handleFragmentLoadError = (hls: any, data: any) => {
    try {
        // HLS.js will automatically retry based on configuration
    } catch (error) {
        console.error('Failed to handle fragment load error:', error);
    }
};

/**
 * Handle manifest load error
 */
const handleManifestLoadError = (hls: any, data: any) => {
    try {
        // Try to reload the manifest
        hls.loadSource(hls.url);
    } catch (error) {
        console.error('Failed to handle manifest load error:', error);
    }
};

/**
 * Handle level load error
 */
const handleLevelLoadError = (hls: any, data: any) => {
    try {
        // Switch to a lower quality level
        const levels = hls.levels;
        if (levels && levels.length > 1) {
            const currentLevel = hls.currentLevel;
            const lowerLevel = Math.max(0, currentLevel - 1);
            hls.nextLevel = lowerLevel;
        }
    } catch (error) {
        console.error('Failed to handle level load error:', error);
    }
};

/**
 * Handle media error
 */
const handleMediaError = (
    hls: any,
    videoElement: HTMLVideoElement,
    data: any
) => {
    try {
        // Try to recover from media error

        // Destroy and recreate HLS instance
        hls.destroy();

        setTimeout(() => {
            loadHLS(videoElement, videoElement.src).catch(console.error);
        }, 1000);
    } catch (error) {
        console.error('Failed to handle media error:', error);
    }
};

/**
 * Handle low buffer stall error specifically
 */
const handleLowBufferStall = (
    hls: any,
    videoElement: HTMLVideoElement,
    errorData?: any
) => {
    try {
        const currentTime = videoElement.currentTime;
        const buffered = videoElement.buffered;
        const duration = videoElement.duration;

        // Strategy 1: If we have buffered content, seek to it
        if (buffered.length > 0) {
            for (let i = 0; i < buffered.length; i++) {
                const start = buffered.start(i);
                const end = buffered.end(i);

                // If current time is before buffered content, seek to start of buffer
                if (currentTime < start && start < end) {
                    videoElement.currentTime = start;

                    // Force play after seeking
                    setTimeout(() => {
                        if (videoElement.paused) {
                            videoElement.play().catch(console.error);
                        }
                    }, 100);
                    return;
                }

                // If we're in a buffered range, seek slightly forward
                if (currentTime >= start && currentTime < end - 0.1) {
                    const seekTime = Math.min(currentTime + 0.1, end - 0.1);
                    videoElement.currentTime = seekTime;
                    return;
                }
            }
        }

        // Strategy 2: If no buffered content or we're at the end, reload stream
        if (buffered.length === 0 || currentTime >= duration - 1) {
            hls.destroy();
            setTimeout(() => {
                loadHLS(videoElement, videoElement.src).catch(console.error);
            }, 1000);
            return;
        }

        // Strategy 3: Force reload current fragment
        if (hls && hls.media) {
            // Try to reload the current fragment by seeking slightly
            const seekTime = Math.max(0, currentTime - 0.1);
            videoElement.currentTime = seekTime;

            // If that doesn't work, try seeking forward
            setTimeout(() => {
                if (videoElement.paused || videoElement.ended) {
                    const forwardSeek = Math.min(
                        currentTime + 0.5,
                        duration - 1
                    );
                    videoElement.currentTime = forwardSeek;
                }
            }, 500);
        }
    } catch (error) {
        console.error('Failed to handle low buffer stall:', error);

        // Last resort: reload the entire stream
        try {
            hls.destroy();
            setTimeout(() => {
                loadHLS(videoElement, videoElement.src).catch(console.error);
            }, 2000);
        } catch (destroyError) {
            console.error('Failed to destroy HLS instance:', destroyError);
        }
    }
};

/**
 * Proactive buffer gap prevention
 */
const preventBufferGap = (videoElement: HTMLVideoElement, hls?: any) => {
    try {
        const currentTime = videoElement.currentTime;
        const buffered = videoElement.buffered;

        // Check if there's a gap between current time and buffered content
        if (buffered.length > 0) {
            for (let i = 0; i < buffered.length; i++) {
                const start = buffered.start(i);
                const end = buffered.end(i);

                const gapSize = start - currentTime;
                if (gapSize > 0 && gapSize < 0.5 && start < end) {
                    videoElement.currentTime = start;

                    // Force play after seeking
                    setTimeout(() => {
                        if (videoElement.paused && !videoElement.ended) {
                            videoElement.play().catch(console.error);
                        }
                    }, 50);
                    return true;
                }
            }
        }

        return false;
    } catch (error) {
        console.error('Failed to prevent buffer gap:', error);
        return false;
    }
};

/**
 * Check if video is experiencing buffer stall
 */
export const isBufferStalled = (videoElement: HTMLVideoElement): boolean => {
    if (!videoElement) return false;

    const currentTime = videoElement.currentTime;
    const buffered = videoElement.buffered;
    const duration = videoElement.duration;

    // If video is paused or ended, it's not stalled
    if (videoElement.paused || videoElement.ended) return false;

    // Check for low buffer stall specifically (gap between current time and buffered content)
    if (buffered.length > 0) {
        for (let i = 0; i < buffered.length; i++) {
            const start = buffered.start(i);
            const end = buffered.end(i);

            // If current time is before buffered content, this is a low buffer stall
            if (currentTime < start && start < end) {
                return true;
            }
        }
    }

    // Check if we have buffered content ahead
    let hasBufferedAhead = false;
    if (buffered.length > 0) {
        for (let i = 0; i < buffered.length; i++) {
            if (buffered.end(i) > currentTime + 1) {
                hasBufferedAhead = true;
                break;
            }
        }
    }

    // If no buffered content ahead and we're not at the end, it's stalled
    return !hasBufferedAhead && currentTime < duration - 1;
};

/**
 * Attempt to recover from buffer stall
 */
export const recoverFromBufferStall = (
    videoElement: HTMLVideoElement,
    hls?: any
): boolean => {
    try {
        const currentTime = videoElement.currentTime;
        const duration = videoElement.duration;
        const buffered = videoElement.buffered;

        // Strategy 1: Only handle SMALL gaps (< 0.5s) - ignore large gaps as HLS.js is buffering ahead
        if (buffered.length > 0) {
            for (let i = 0; i < buffered.length; i++) {
                const start = buffered.start(i);
                const end = buffered.end(i);

                // Check if we're already IN this buffered range - no action needed
                if (currentTime >= start && currentTime < end) {
                    return false; // Already in buffered range, let HLS.js handle it
                }

                // Only seek to small gaps (< 0.5s)
                const gapSize = start - currentTime;
                if (gapSize > 0 && gapSize < 0.5 && start < end) {
                    videoElement.currentTime = start;
                    return true;
                }
            }
        }

        // Strategy 2: Switch to lower quality if HLS is available
        if (hls && hls.levels && hls.levels.length > 1) {
            const currentLevel = hls.currentLevel;
            const lowerLevel = Math.max(0, currentLevel - 1);

            if (lowerLevel !== currentLevel) {
                hls.nextLevel = lowerLevel;
                return true;
            }
        }

        return false;
    } catch (error) {
        console.error('Buffer stall recovery failed:', error);
        return false;
    }
};

/**
 * Monitor video for buffer stalls and auto-recover
 */
export const startBufferStallMonitoring = (
    videoElement: HTMLVideoElement,
    hls?: any,
    onStallDetected?: () => void
): (() => void) => {
    let monitoringInterval: NodeJS.Timeout | null = null;
    let lastStallTime = 0;

    const checkForStall = () => {
        if (isBufferStalled(videoElement)) {
            const now = Date.now();

            // Only recover if we haven't tried recently
            if (now - lastStallTime > 5000) {
                lastStallTime = now;

                if (onStallDetected) {
                    onStallDetected();
                }

                recoverFromBufferStall(videoElement, hls);
            }
        }
    };

    // Start monitoring
    monitoringInterval = setInterval(checkForStall, 1000);

    // Return cleanup function
    return () => {
        if (monitoringInterval) {
            clearInterval(monitoringInterval);
            monitoringInterval = null;
        }
    };
};

/**
 * Extract YouTube video ID from various YouTube URL formats
 */
export const extractYouTubeVideoId = (url: string): string | null => {
    const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
        /youtube\.com\/shorts\/([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
            return match[1];
        }
    }

    return null;
};

/**
 * Check if a YouTube URL is a YouTube Shorts URL
 */
export const isYouTubeShorts = (url: string): boolean => {
    return url.includes('/shorts/');
};

/**
 * Generate YouTube embed URL
 */
export const generateYouTubeEmbedUrl = (
    videoId: string,
    options: {
        autoplay?: boolean;
        muted?: boolean;
        controls?: boolean;
        modestbranding?: boolean;
        rel?: boolean;
    } = {}
): string => {
    const params = new URLSearchParams();

    if (options.autoplay) params.set('autoplay', '1');
    if (options.muted) params.set('mute', '1');

    params.set('controls', '1');
    params.set('fs', '0');
    params.set('playsinline', '1');
    params.set('enablejsapi', '1');
    params.set('color', 'red');
    params.set('showinfo', '0');
    params.set('iv_load_policy', '3');
    params.set('modestbranding', '1');
    params.set('rel', '0');
    params.set('autoplay', '1');
    params.set('loop', '1');
    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

/**
 * Generate YouTube thumbnail URL
 */
export const generateYouTubeThumbnail = (
    videoId: string,
    quality: 'default' | 'medium' | 'high' | 'standard' | 'maxres' = 'high'
): string => {
    const qualityMap = {
        default: 'default',
        medium: 'mqdefault',
        high: 'hqdefault',
        standard: 'sddefault',
        maxres: 'maxresdefault',
    };

    return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
};

/**
 * Extract Vimeo video ID from URL
 */
export const extractVimeoVideoId = (url: string): string | null => {
    const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return match ? match[1] : null;
};

/**
 * Generate Vimeo embed URL
 */
export const generateVimeoEmbedUrl = (
    videoId: string,
    options: {
        autoplay?: boolean;
        muted?: boolean;
        controls?: boolean;
    } = {}
): string => {
    const params = new URLSearchParams();

    if (options.autoplay) params.set('autoplay', '1');
    if (options.muted) params.set('muted', '1');
    if (options.controls === false) params.set('controls', '0');

    return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
};

/**
 * Detect video source type from URL
 */
export const detectVideoSourceType = (url: string): VideoSourceType => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
        return isYouTubeShorts(url)
            ? VideoSourceType.YOUTUBE_SHORTS
            : VideoSourceType.YOUTUBE;
    }

    if (url.includes('vimeo.com')) {
        return VideoSourceType.VIMEO;
    }

    // Assume S3 or direct video URL if not YouTube or Vimeo
    return VideoSourceType.S3_DIRECT;
};

/**
 * Generate video embed URL based on source type
 */
export const generateEmbedUrl = (
    video: VideoSource,
    options: {
        autoplay?: boolean;
        muted?: boolean;
        controls?: boolean;
    } = {}
): string => {
    switch (video.type) {
        case VideoSourceType.YOUTUBE:
        case VideoSourceType.YOUTUBE_SHORTS: {
            const videoId = extractYouTubeVideoId(video.url);
            if (!videoId) throw new Error('Invalid YouTube URL');
            return generateYouTubeEmbedUrl(videoId, options);
        }

        case VideoSourceType.VIMEO: {
            const videoId = extractVimeoVideoId(video.url);
            if (!videoId) throw new Error('Invalid Vimeo URL');
            return generateVimeoEmbedUrl(videoId, options);
        }

        case VideoSourceType.S3_DIRECT:
        default:
            return video.url;
    }
};

/**
 * Generate thumbnail URL based on source type
 */
export const generateThumbnailUrl = (
    video: VideoSource,
    thumbnail?: string
): string => {
    const source = detectVideoSourceType(video.url);
    if (thumbnail && source !== VideoSourceType.S3_DIRECT) return thumbnail;

    switch (source) {
        case VideoSourceType.YOUTUBE:
        case VideoSourceType.YOUTUBE_SHORTS: {
            const videoId = extractYouTubeVideoId(video.url);
            return videoId ? generateYouTubeThumbnail(videoId) : '';
        }

        case VideoSourceType.VIMEO:
            // Vimeo thumbnails require API call, return placeholder
            return '/placeholder-video.jpg';

        case VideoSourceType.S3_DIRECT:
            const path = thumbnail?.substring(
                thumbnail.indexOf('/video/'),
                thumbnail.lastIndexOf('/default')
            );
            const filename = thumbnail
                ?.substring(thumbnail.lastIndexOf('/') + 1)
                .replace('.png', '.webp');
            const newURL = `https://ackodrive-prod.ackoassets.com${path}/image_cdn/medium/${filename}`;
            return path && filename ? newURL : (thumbnail ?? '');
        default:
            return '/placeholder-video.jpg';
    }
};

/**
 * Format time in seconds to MM:SS or HH:MM:SS format
 */
export const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || seconds < 0) return '00:00';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    return `${minutes.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`;
};

/**
 * Parse time string (MM:SS or HH:MM:SS) to seconds
 */
export const parseTimeToSeconds = (timeString: string): number => {
    const parts = timeString.split(':').map(Number);

    if (parts.length === 2) {
        // MM:SS format
        return parts[0] * 60 + parts[1];
    } else if (parts.length === 3) {
        // HH:MM:SS format
        return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }

    return 0;
};

/**
 * Calculate progress percentage from current time and duration
 */
export const calculateProgress = (
    currentTime: number,
    duration: number
): number => {
    if (duration === 0 || isNaN(currentTime) || isNaN(duration)) return 0;
    return Math.min(100, Math.max(0, (currentTime / duration) * 100));
};

/**
 * Get video file extension from URL
 */
export const getVideoFileExtension = (url: string): string => {
    const match = url.match(/\.([^.?]+)(?:\?|$)/);
    return match ? match[1].toLowerCase() : '';
};

/**
 * Check if browser supports video format
 */
export const supportsVideoFormat = (format: string): boolean => {
    if (typeof document === 'undefined') return false;

    const video = document.createElement('video');
    const mimeTypes: Record<string, string> = {
        mp4: 'video/mp4',
        webm: 'video/webm',
        ogg: 'video/ogg',
        mov: 'video/quicktime',
        avi: 'video/x-msvideo',
        m3u8: 'application/vnd.apple.mpegurl',
        m3u: 'application/vnd.apple.mpegurl',
    };

    const mimeType = mimeTypes[format.toLowerCase()];
    if (mimeType) {
        return video.canPlayType(mimeType) !== '';
    }

    // For HLS formats, check HLS support
    if (format.toLowerCase() === 'm3u8' || format.toLowerCase() === 'm3u') {
        return supportsHLS();
    }

    return false;
};

/**
 * Detect device type based on screen width
 */
export const detectDeviceType = (): 'mobile' | 'desktop' => {
    if (typeof window === 'undefined') return 'desktop';
    return window.innerWidth <= 768 ? 'mobile' : 'desktop';
};

/**
 * Get optimal video quality based on device and connection
 */
export const getOptimalVideoQuality = (
    deviceType: 'mobile' | 'desktop'
): VideoQuality => {
    // Simple logic - can be enhanced with network speed detection
    if (typeof navigator !== 'undefined' && 'connection' in navigator) {
        const connection = (navigator as any).connection;
        if (connection) {
            const effectiveType = connection.effectiveType;

            if (effectiveType === 'slow-2g' || effectiveType === '2g') {
                return VideoQuality.LOW;
            } else if (effectiveType === '3g') {
                return deviceType === 'mobile'
                    ? VideoQuality.MEDIUM
                    : VideoQuality.HIGH;
            }
        }
    }

    return deviceType === 'mobile' ? VideoQuality.MEDIUM : VideoQuality.HIGH;
};

/**
 * Create video source object from URL
 */
export const createVideoSource = (
    url: string,
    id?: string,
    quality?: VideoQuality
): any => {
    return {
        id:
            id ||
            `video-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: detectVideoSourceType(url),
        url,
        quality: quality || VideoQuality.AUTO,
    };
};

/**
 * Validate video URL
 */
export const isValidVideoUrl = (url: string): boolean => {
    try {
        new URL(url);
        const type = detectVideoSourceType(url);

        switch (type) {
            case VideoSourceType.YOUTUBE:
            case VideoSourceType.YOUTUBE_SHORTS:
                return extractYouTubeVideoId(url) !== null;

            case VideoSourceType.VIMEO:
                return extractVimeoVideoId(url) !== null;

            case VideoSourceType.S3_DIRECT:
                // For direct video URLs, check common video file extensions
                const extension = getVideoFileExtension(url);
                return ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(extension);

            default:
                return false;
        }
    } catch {
        return false;
    }
};
